import React, { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { MessageSquare, Bell, X, Check, Ticket, Wifi, Clock, User, Volume2, VolumeX } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { playCommentSound, playBroadcastSound, isMuted, setMuted } from '../../lib/notificationSounds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_LABELS = {
  superadmin: 'SuperAdmin',
  admin: 'Admin',
  operator: 'Operator',
  technician: 'Texnik',
  accountant: 'Mühasib',
  d2d_agent: 'D2D Agent',
  lawyer: 'Hüquqşunas',
  network_admin: 'NetAdmin',
  system_admin: 'SysAdmin',
};

function timeAgo(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const sec = Math.floor(diff / 1000);
  if (sec < 60) return `${sec} san əvvəl`;
  const min = Math.floor(sec / 60);
  if (min < 60) return `${min} dəq əvvəl`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr} saat əvvəl`;
  const day = Math.floor(hr / 24);
  return `${day} gün əvvəl`;
}

function timeUntil(iso) {
  if (!iso) return '';
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return 'bitdi';
  const sec = Math.floor(diff / 1000);
  const min = Math.floor(sec / 60);
  const hr = Math.floor(min / 60);
  const day = Math.floor(hr / 24);
  if (day > 0) return `${day} gün ${hr % 24} saat qaldı`;
  if (hr > 0) return `${hr} saat ${min % 60} dəq qaldı`;
  return `${min} dəq qaldı`;
}

function formatDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('az-AZ', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
}

/**
 * Universal in-app notification bell.
 * Props:
 *  - type: 'comment' | 'broadcast'
 *  - pollIntervalMs: default 25000
 *  - onCommentClick: ({ entity_type, entity_id }) => void  (only for type=comment)
 */
export default function NotificationBell({ type, pollIntervalMs = 25000, onCommentClick }) {
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };
  const [items, setItems] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);
  const [muted, setMutedState] = useState(isMuted());
  const prevCountRef = useRef(0);
  const hasLoadedOnceRef = useRef(false);
  const originalTitleRef = useRef(document.title);

  const fetchItems = useCallback(async () => {
    try {
      if (type === 'comment') {
        const res = await axios.get(`${API}/notifications/inbox/comments`, { headers });
        const list = res.data || [];
        setItems(list);
        setUnreadCount(list.filter((i) => !i.read_at).length);
      } else if (type === 'broadcast') {
        const res = await axios.get(`${API}/broadcast/active`, { headers });
        const list = res.data || [];
        setItems(list);
        setUnreadCount(list.length);
      }
    } catch (e) {
      // silent fail; UI keeps last state
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  // Initial + interval polling
  useEffect(() => {
    fetchItems();
    const id = setInterval(fetchItems, pollIntervalMs);
    return () => clearInterval(id);
  }, [fetchItems, pollIntervalMs]);

  // Title flash + sound when new notifications arrive
  useEffect(() => {
    if (unreadCount > prevCountRef.current && unreadCount > 0) {
      document.title = `(${unreadCount}) ${originalTitleRef.current}`;
      // Play sound only AFTER the first successful fetch (not on initial mount)
      if (hasLoadedOnceRef.current) {
        if (type === 'comment') {
          playCommentSound();
        } else if (type === 'broadcast') {
          playBroadcastSound();
        }
      }
    } else if (unreadCount === 0) {
      document.title = originalTitleRef.current;
    } else {
      document.title = `(${unreadCount}) ${originalTitleRef.current}`;
    }
    prevCountRef.current = unreadCount;
    hasLoadedOnceRef.current = true;
  }, [unreadCount, type]);

  // Reset title on unmount
  useEffect(() => {
    return () => {
      document.title = originalTitleRef.current;
    };
  }, []);

  const handleMarkOneRead = async (id) => {
    try {
      await axios.post(`${API}/notifications/inbox/${id}/read`, {}, { headers });
      setItems((prev) => prev.map((i) => (i.id === id ? { ...i, read_at: new Date().toISOString() } : i)));
      setUnreadCount((c) => Math.max(0, c - 1));
    } catch (e) {
      // ignore
    }
  };

  const handleMarkAllRead = async () => {
    try {
      await axios.post(`${API}/notifications/inbox/read-all`, {}, { headers });
      setItems((prev) => prev.map((i) => ({ ...i, read_at: new Date().toISOString() })));
      setUnreadCount(0);
    } catch (e) {
      // ignore
    }
  };

  const handleCommentClick = (item) => {
    if (!item.read_at) {
      handleMarkOneRead(item.id);
    }
    setOpen(false);
    if (onCommentClick) {
      onCommentClick({ entity_type: item.entity_type, entity_id: item.entity_id });
    }
  };

  const isComment = type === 'comment';
  const Icon = isComment ? MessageSquare : Bell;
  const colorClass = isComment ? 'text-blue-600' : 'text-purple-600';
  const badgeClass = isComment ? 'bg-blue-600' : 'bg-purple-600';
  const buttonTitle = isComment ? 'İş bildirişləri (Şərhlər)' : 'Elanlar';

  return (
    <>
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            size="icon"
            className="relative"
            title={buttonTitle}
            data-testid={`notification-bell-${type}`}
          >
            <Icon className={`h-5 w-5 ${colorClass}`} />
            {unreadCount > 0 && (
              <span
                className={`absolute -top-1 -right-1 ${badgeClass} text-white text-xs rounded-full min-w-[18px] h-[18px] flex items-center justify-center px-1`}
                data-testid={`notification-badge-${type}`}
              >
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent
          className="w-96 p-0"
          align="end"
          data-testid={`notification-popover-${type}`}
        >
          <div className="flex items-center justify-between px-4 py-3 border-b">
            <div className="font-semibold flex items-center gap-2">
              <Icon className={`h-4 w-4 ${colorClass}`} />
              {isComment ? 'Yeni Şərhlər' : 'Aktiv Bildirişlər'}
              {unreadCount > 0 && (
                <Badge variant="secondary" className="text-xs">
                  {unreadCount}
                </Badge>
              )}
            </div>
            <div className="flex items-center gap-1">
              <Button
                size="sm"
                variant="ghost"
                onClick={() => {
                  const next = !muted;
                  setMuted(next);
                  setMutedState(next);
                  // Demo play when unmuting so user knows it works
                  if (!next) {
                    if (type === 'comment') playCommentSound();
                    else playBroadcastSound();
                  }
                }}
                className="h-7 w-7 p-0"
                title={muted ? 'Səsi açın' : 'Səsi söndürün'}
                data-testid={`mute-toggle-${type}`}
              >
                {muted ? (
                  <VolumeX className="h-4 w-4 text-zinc-400" />
                ) : (
                  <Volume2 className="h-4 w-4 text-zinc-600" />
                )}
              </Button>
              {isComment && unreadCount > 0 && (
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={handleMarkAllRead}
                  className="text-xs h-7"
                  data-testid="mark-all-read-btn"
                >
                  <Check className="h-3 w-3 mr-1" /> Hamısını oxu
                </Button>
              )}
            </div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {items.length === 0 && (
              <div className="py-10 text-center text-sm text-zinc-500">
                {isComment ? 'Yeni şərh yoxdur' : 'Aktiv bildiriş yoxdur'}
              </div>
            )}
            {isComment &&
              items.map((it) => (
                <button
                  key={it.id}
                  onClick={() => handleCommentClick(it)}
                  className={`w-full text-left px-4 py-3 border-b hover:bg-zinc-50 transition-colors ${
                    !it.read_at ? 'bg-blue-50/40' : ''
                  }`}
                  data-testid={`comment-item-${it.id}`}
                >
                  <div className="flex items-center gap-2 mb-1">
                    {it.entity_type === 'ticket' ? (
                      <Badge className="bg-blue-100 text-blue-800 hover:bg-blue-100 text-xs">
                        <Ticket className="h-3 w-3 mr-1" /> SERVİS
                      </Badge>
                    ) : (
                      <Badge className="bg-emerald-100 text-emerald-800 hover:bg-emerald-100 text-xs">
                        <Wifi className="h-3 w-3 mr-1" /> QOŞULMA
                      </Badge>
                    )}
                    {!it.read_at && (
                      <span className="w-2 h-2 rounded-full bg-blue-600 inline-block" />
                    )}
                  </div>
                  <div className="font-medium text-sm">{it.title}</div>
                  <div className="text-xs text-zinc-600 mt-0.5 line-clamp-2">{it.body}</div>
                  <div className="flex items-center gap-2 mt-1.5 text-xs text-zinc-500">
                    <Clock className="h-3 w-3" />
                    {timeAgo(it.created_at)}
                    {it.extra?.customer && (
                      <>
                        <span>•</span>
                        <span className="truncate">{it.extra.customer}</span>
                      </>
                    )}
                  </div>
                </button>
              ))}

            {!isComment &&
              items.map((it) => (
                <button
                  key={it.id}
                  onClick={() => setDetail(it)}
                  className="w-full text-left px-4 py-3 border-b hover:bg-zinc-50 transition-colors"
                  data-testid={`broadcast-item-${it.id}`}
                >
                  <div className="font-medium text-sm">{it.title}</div>
                  <div className="text-xs text-zinc-600 mt-1 line-clamp-2">{it.body}</div>
                  <div className="mt-2 pt-2 border-t border-zinc-100 text-xs space-y-1">
                    <div className="flex items-center gap-1.5 text-zinc-600">
                      <User className="h-3 w-3" />
                      <span className="font-medium">{it.sent_by_name || '—'}</span>
                      {it.sender_role && (
                        <Badge variant="outline" className="text-[10px] py-0 px-1.5 ml-1">
                          {ROLE_LABELS[it.sender_role] || it.sender_role}
                        </Badge>
                      )}
                    </div>
                    <div className="text-zinc-500">⏱ Bitir: {timeUntil(it.expires_at)}</div>
                  </div>
                </button>
              ))}
          </div>
        </PopoverContent>
      </Popover>

      {/* Broadcast detail modal */}
      <Dialog open={!!detail} onOpenChange={(v) => !v && setDetail(null)}>
        <DialogContent className="max-w-lg" data-testid="broadcast-detail-modal">
          <DialogHeader>
            <DialogTitle>{detail?.title}</DialogTitle>
          </DialogHeader>
          {detail && (
            <div className="space-y-4">
              <div className="text-sm text-zinc-800 whitespace-pre-wrap">{detail.body}</div>
              <div className="pt-3 border-t border-zinc-200 space-y-1.5 text-sm">
                <div className="flex items-center gap-2 text-zinc-700">
                  <User className="h-4 w-4 text-zinc-500" />
                  <span className="font-medium">{detail.sent_by_name || '—'}</span>
                  {detail.sender_role && (
                    <Badge variant="outline" className="text-xs">
                      {ROLE_LABELS[detail.sender_role] || detail.sender_role}
                    </Badge>
                  )}
                </div>
                <div className="text-zinc-600">
                  📅 Göndərilib: {formatDateTime(detail.scheduled_at || detail.created_at)}
                </div>
                <div className="text-zinc-600">
                  ⏱ Sona çatır: {formatDateTime(detail.expires_at)} ({timeUntil(detail.expires_at)})
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
}
